import {DateRangePreset} from "../../../general/DateRangeInput/constants.ts";
import {
    formatDateForInput,
    getPresetRange,
} from "../../../general/DateRangeInput/utils/dateRangeUtils.ts";
import {ComparisonMode, type DateRangeStrings} from "./comparisonPeriods.ts";
import {ViewModeEnum} from "../types/types.ts";

const PREFS_KEY_PREFIX = "pocketcfo.trialBalance.prefs";

export interface TrialBalancePrefs {
    viewMode: ViewModeEnum;
    predictedAccounts: boolean;
    comparison: ComparisonMode;
    dateRange: DateRangeStrings;
    periodicYtdMonthIndex: number | null;
}

function storageKey(userId: string, businessId: string): string {
    return `${PREFS_KEY_PREFIX}:${userId}:${businessId}`;
}

function getDefaultDateRange(): DateRangeStrings {
    const yearRange = getPresetRange(DateRangePreset.ThisYear);
    return {
        from: formatDateForInput(yearRange.from),
        to: formatDateForInput(yearRange.to),
    };
}

function defaultPrefs(): TrialBalancePrefs {
    return {
        viewMode: ViewModeEnum.SLIM,
        predictedAccounts: false,
        comparison: ComparisonMode.None,
        dateRange: getDefaultDateRange(),
        periodicYtdMonthIndex: null,
    };
}

function isViewMode(value: unknown): value is ViewModeEnum {
    return value === ViewModeEnum.SLIM || value === ViewModeEnum.CLASSIC;
}

function isComparisonMode(value: unknown): value is ComparisonMode {
    return Object.values(ComparisonMode).includes(value as ComparisonMode);
}

function isDateRangeStrings(value: unknown): value is DateRangeStrings {
    return (
        typeof value === "object" &&
        value !== null &&
        !!(value as DateRangeStrings).from &&
        !!(value as DateRangeStrings).to &&
        typeof (value as DateRangeStrings).from === "string" &&
        typeof (value as DateRangeStrings).to === "string"
    );
}

function isValidYtdMonthIndex(value: unknown): value is number {
    return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

function sanitizePrefs(parsed: unknown): TrialBalancePrefs {
    const fallback = defaultPrefs();
    if (typeof parsed !== "object" || parsed === null) return fallback;
    const candidate = parsed as Partial<TrialBalancePrefs>;
    return {
        viewMode: isViewMode(candidate.viewMode) ? candidate.viewMode : fallback.viewMode,
        predictedAccounts:
            typeof candidate.predictedAccounts === "boolean"
                ? candidate.predictedAccounts
                : fallback.predictedAccounts,
        comparison: isComparisonMode(candidate.comparison)
            ? candidate.comparison
            : fallback.comparison,
        dateRange: isDateRangeStrings(candidate.dateRange)
            ? candidate.dateRange
            : fallback.dateRange,
        periodicYtdMonthIndex: isValidYtdMonthIndex(candidate.periodicYtdMonthIndex)
            ? candidate.periodicYtdMonthIndex
            : fallback.periodicYtdMonthIndex,
    };
}

export function getStoredPrefs(userId: string, businessId: string): TrialBalancePrefs {
    let raw: string | null;
    try {
        raw = localStorage.getItem(storageKey(userId, businessId));
    } catch {
        return defaultPrefs();
    }
    if (!raw) return defaultPrefs();
    try {
        return sanitizePrefs(JSON.parse(raw));
    } catch {
        return defaultPrefs();
    }
}

export function storePrefs(
    userId: string,
    businessId: string,
    prefs: TrialBalancePrefs,
): void {
    const key = storageKey(userId, businessId);
    try {
        localStorage.setItem(key, JSON.stringify(prefs));
    } catch (e) {
        console.error(`Failed to save preference ${key}:`, e);
    }
}
